import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import cx from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Box, Grid, Typography, Stack } from '@mui/material';
import { getWindow, getEntitlements } from '@surfline/web-common';

import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import CamCompanionAd from 'components/CamCompanionAd';
import CameraInsights from 'components/CameraInsights';
import { PremiumCamLabel } from 'components/CTAs';
import { CameraInsightsIcon, PlayIcon } from 'components/Icons';
import InternalCamBadge from 'components/InternalCamBadge';
import WavetrakLink from 'components/WavetrakLink';
import config from 'config';
import useCameraInsightsCamera from 'hooks/useCameraInsightsCamera';
import useCheckCameraSupportsSmartRewinds from 'hooks/useCheckCameraSupportsSmartRewinds';
import { useUserPermissionStatus } from 'selectors/user';
import type { Camera } from 'types/camera';
import { getParsedCameraTitle } from 'utils/camera';
import { SpotDetails } from 'types/spot';
import useCamPlayerGrid from 'hooks/useCamPlayerGrid';
import { useAppSelector } from 'stores/hooks';
import { useTreatments } from 'utils/treatments';
import { getIsPremiumWithAdsUser } from 'utils/user';

import styles from './RewindsAndInsightsCamsAndAnglesContainer.module.scss';

interface CamAngleProps {
  camera: Camera;
  selectedCamera: Camera;
  setPrimaryCamId: (id: string) => void;
  spot: SpotDetails;
}

const CamAngle: React.FC<CamAngleProps> = ({ camera, selectedCamera, setPrimaryCamId, spot }) => {
  const router = useRouter();
  const treatments = useTreatments();
  const paywallRedesignTreatment = treatments[SL_WEB_CAM_PAYWALL_REDESIGN];
  const supportsSmartRewinds = useCheckCameraSupportsSmartRewinds(camera, spot);
  const { hasPremiumCamPermissions, hasAdFreeCamPermissions, hasAdSupportedCamPermissions } =
    useUserPermissionStatus();
  const hasLiveCamPermissions =
    hasAdFreeCamPermissions || hasAdSupportedCamPermissions || paywallRedesignTreatment === 'off';

  const isSelectedCam = camera._id === selectedCamera._id;

  const camThumbnailWrapperClass = cx({
    [styles.camAngleThumbnailWrapper]: true,
    [styles.selectedCamera]: isSelectedCam,
    [styles.supportsSmartRewinds]: supportsSmartRewinds,
  });

  const camAnglesNameTextClass = cx({
    [styles.camAngleNameText]: true,
    [styles.selectedCamera]: camera._id === selectedCamera._id,
  });

  const parsedCameraTitle = useMemo(() => getParsedCameraTitle(camera.title), [camera.title]);

  const camAngleUrl = useMemo(() => {
    const url = new URL(router.asPath, config.surflineHost);
    const params = new URLSearchParams(url.search);
    params.set('camId', camera._id);
    url.search = params.toString();
    return url.toString();
  }, [camera._id, router.asPath]);

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setPrimaryCamId(camera._id);
    },
    [camera._id, setPrimaryCamId],
  );

  return (
    <WavetrakLink
      className={styles.camAngle}
      data-testid={`cam-angle-box-${camera.title}`.replace(/\s/g, '-')}
      onClick={onClickHandler}
      href={camAngleUrl}
    >
      <div className={camThumbnailWrapperClass} data-testid="cam-angle-thumbnail-wrapper">
        {camera.isInternalCam && <InternalCamBadge />}
        {!hasPremiumCamPermissions && camera.isPremium && paywallRedesignTreatment === 'off' && (
          <div className={styles.labelPremium}>
            <PremiumCamLabel />
          </div>
        )}
        <Box
          className={cx(styles.camAngleThumbnail, {
            [styles.camAngleThumbnailBlur]: !hasLiveCamPermissions,
          })}
        >
          <Image
            alt={camera.title}
            layout="fill"
            loading="lazy"
            src={camera.stillUrl}
            unoptimized
          />
        </Box>
        {!isSelectedCam && (
          <div className={styles.icon}>
            <PlayIcon />
          </div>
        )}
      </div>
      {supportsSmartRewinds && (
        <div className={styles.lozenge}>
          <CameraInsightsIcon />
        </div>
      )}
      <Box>
        <Typography className={camAnglesNameTextClass} variant="callout2" component="p">
          {parsedCameraTitle}
        </Typography>
      </Box>
    </WavetrakLink>
  );
};

interface RewindsAndInsightsCamsAndAnglesContainerProps {
  cameras: Array<Camera>;
  children?: ReactNode;
  disablePageAds?: boolean;
  isSpotPage?: boolean;
  multiCamViewActive?: boolean;
  selectedCamera: Camera;
  setPrimaryCamId: (id: string) => void;
  spot: SpotDetails;
}

const camInsightsContainerClass = cx({
  'sl-section-container': true,
  [styles.cameraInsightsContainer]: true,
});

const RewindsAndInsightsCamsAndAnglesContainer: React.FC<
  RewindsAndInsightsCamsAndAnglesContainerProps
> = ({
  cameras,
  children,
  disablePageAds,
  isSpotPage,
  multiCamViewActive,
  selectedCamera,
  setPrimaryCamId,
  spot,
}) => {
  const win = getWindow();
  const cameraInsightsCamera = useCameraInsightsCamera({ selectedCamera, spot });

  const { hasPremiumCamPermissions } = useUserPermissionStatus();
  const entitlements = useAppSelector(getEntitlements, shallowEqual);
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);

  const [adSlotRendered, setAdSlotRendered] = useState(false);
  // check against tcf api and set accordingly
  const [displayBasicAds, setDisplayBasicAds] = useState(true);

  const hasAngles = cameras?.length > 1;
  // decide if the ad slot should be rendered based on the UI and user subscription
  const shouldRenderCompanionAd =
    !multiCamViewActive &&
    (!hasPremiumCamPermissions || isPremiumWithAdsUser) &&
    disablePageAds !== true;
  // decide if companion ad is available based on the ad SDKs
  const isCompanionAdAvailable = adSlotRendered && displayBasicAds;

  const camPlayerGridState = useCamPlayerGrid({
    camerasLength: cameras.length,
    hasAngles,
    multiCamViewActive: !!multiCamViewActive,
    shouldRenderCompanionAd,
  });

  const spotHasACamThatSupportsInsights = useMemo(
    () => !!spot.cameras.find((cam) => cam.supportsInsights),
    [spot],
  );

  const cameraAngles = useMemo(
    () =>
      hasAngles ? (
        <Grid
          item
          mobile={camPlayerGridState.items[2].mobile}
          tabletLg={camPlayerGridState.items[2].tabletLg}
          desktop={camPlayerGridState.items[2].desktop}
          desktopMd={camPlayerGridState.items[2].desktopMd}
          desktopLg={camPlayerGridState.items[2].desktopLg}
        >
          <Stack
            className={cx(styles.camAngleContainer, 'cam-angles')}
            direction={{ tabletLg: 'column', mobile: 'row' }}
            spacing={{ tabletLg: 2, mobile: 2 }}
            data-testid="cam-angles-container"
          >
            {cameras.map((camera: Camera) => (
              <CamAngle
                camera={camera}
                key={camera.title.replace(/\s/g, '-')}
                setPrimaryCamId={setPrimaryCamId}
                selectedCamera={selectedCamera}
                spot={spot}
              />
            ))}
          </Stack>
        </Grid>
      ) : null,
    [camPlayerGridState.items, cameras, hasAngles, selectedCamera, setPrimaryCamId, spot],
  );

  /**
   * if we're in a GDPR region (UK) we need to check the result of the tcf API callback
   * check to see if tcData.eventStatus = 'cmpuishow', this means the user has yet to perform an consent action
   * check to see if the user has specifically objected to legitimate interests for basic ads
   * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#addeventlistener
   */
  const tcfApiCallback = useCallback(
    (
      tcData: {
        eventStatus: 'cmpuishown' | 'useractioncomplete' | 'tcloaded';
        gdprApplies: boolean;
        purpose: {
          legitimateInterests: boolean[];
        };
      },
      success: boolean,
    ) => {
      if (tcData.gdprApplies) {
        if (
          success &&
          (tcData.eventStatus === 'cmpuishown' || !tcData?.purpose?.legitimateInterests?.[2])
        ) {
          setDisplayBasicAds(false);
        } else {
          setDisplayBasicAds(true);
        }
      }
    },
    [],
  );

  // listen for changes in the rendering of the ad slots and tcf settings (if present)
  useEffect(() => {
    const adQueuePush = (cb: () => void) => {
      if (win?.freestar?.queue?.push) {
        win.freestar.queue.push(cb);
      } else {
        win?.googletag?.cmd?.push?.(cb);
      }
    };

    // Freestar manages slot collapse internally — show the companion ad wrapper
    // so Freestar can fill or collapse the inner slot on its own.
    setAdSlotRendered(true);
    adQueuePush(() => {
      win?.__tcfapi?.('addEventListener', 2, tcfApiCallback);
    });

    return () => {
      adQueuePush(() => {
        win?.__tcfapi?.('removeEventListener', 2, tcfApiCallback);
      });
    };
  }, [win, tcfApiCallback]);

  return (
    <Box
      className={cx({
        [styles.container]: true,
        [styles.containerWithoutInsights]: isSpotPage && !cameraInsightsCamera,
      })}
    >
      <Box className="sl-section-container">
        <Box className="sl-section-full-bleed">
          <Grid
            container
            columns={{
              mobile: camPlayerGridState.container.mobile,
              tabletLg: camPlayerGridState.container.tabletLg,
              desktop: camPlayerGridState.container.desktop,
              desktopMd: camPlayerGridState.container.desktopMd,
              desktopLg: camPlayerGridState.container.desktopLg,
            }}
            columnSpacing={2}
            justifyContent="space-between"
            className={cx({
              [styles.grid]: true,
              [styles.gridWithAngles]: hasAngles,
              [styles.gridWithCompanionAd]: shouldRenderCompanionAd,
              [styles.gridWithMultiCam]: multiCamViewActive,
              [styles.gridWithMultiCam4]: multiCamViewActive && cameras.length === 4,
            })}
          >
            {multiCamViewActive ? (
              <>
                <Grid
                  item
                  tabletLg={camPlayerGridState.items[0].tabletLg}
                  desktop={camPlayerGridState.items[0].desktop}
                  desktopMd={camPlayerGridState.items[0].desktopMd}
                  desktopLg={camPlayerGridState.items[0].desktopLg}
                  className={styles.gridLeftPadding}
                />
                <Grid
                  item
                  data-testid="multicam-container"
                  tabletLg={camPlayerGridState.items[1].tabletLg}
                  desktop={camPlayerGridState.items[1].desktop}
                  desktopMd={camPlayerGridState.items[1].desktopMd}
                  desktopLg={camPlayerGridState.items[1].desktopLg}
                >
                  {children}
                </Grid>
                <Grid
                  item
                  tabletLg={camPlayerGridState.items[3].tabletLg}
                  desktop={camPlayerGridState.items[3].desktop}
                  desktopMd={camPlayerGridState.items[3].desktopMd}
                  desktopLg={camPlayerGridState.items[3].desktopLg}
                  className={styles.gridRightPadding}
                />
              </>
            ) : (
              <>
                <Grid
                  item
                  tabletLg={camPlayerGridState.items[0].tabletLg}
                  desktop={camPlayerGridState.items[0].desktop}
                  desktopMd={camPlayerGridState.items[0].desktopMd}
                  desktopLg={camPlayerGridState.items[0].desktopLg}
                  className={styles.gridLeftPadding}
                />
                <Grid
                  item
                  data-testid="insights-container"
                  mobile={camPlayerGridState.items[1].mobile}
                  tabletLg={camPlayerGridState.items[1].tabletLg}
                  desktop={camPlayerGridState.items[1].desktop}
                  desktopMd={camPlayerGridState.items[1].desktopMd}
                  desktopLg={camPlayerGridState.items[1].desktopLg}
                >
                  {children}
                </Grid>
                {cameraAngles}
                <Grid
                  item
                  tabletLg={camPlayerGridState.items[3].tabletLg}
                  desktop={camPlayerGridState.items[3].desktop}
                  desktopMd={camPlayerGridState.items[3].desktopMd}
                  desktopLg={camPlayerGridState.items[3].desktopLg}
                  className={styles.gridRightPadding}
                />
                <Grid
                  item
                  desktopMd={camPlayerGridState.items[4].desktopMd}
                  desktopLg={camPlayerGridState.items[4].desktopLg}
                  className={styles.gridCompanionAd}
                >
                  {shouldRenderCompanionAd && (
                    <CamCompanionAd
                      style={{ display: isCompanionAdAvailable ? 'block' : 'none' }}
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>
      {spotHasACamThatSupportsInsights && cameraInsightsCamera ? (
        <Box className={camInsightsContainerClass} data-testid="camera-insights-container">
          <Box className="sl-section-full-bleed">
            <CameraInsights camera={cameraInsightsCamera} camPlayerGridState={camPlayerGridState} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default RewindsAndInsightsCamsAndAnglesContainer;
